<template>
  <div id="page-container" v-loading="this.loadingQuestionnaire">
    <div class="pageBodyContainer">
      <QuestionnaireHeader />
      <div class="questionnaireContent">
        <div class="questionnaireDivider mt-[18px] mb-[16px]" />
        <div class="subTitleContainer w-full">
          <div class="subTitle">PATIENT INFORMATION</div>
        </div>
        <div class="subpartContainer">
          <div class="title">Date of Birth</div>
          <el-input
            v-model="this.birthDateToUpdate"
            placeholder="MM/DD/YYYY"
            class="inputField"
            @blur="handleBirthDateChange"
            :disabled="this.patientHasDOB || this.qnrIsCompleted"
          />
        </div>
        <div class="subpartContainer">
          <QuestionnaireRadio
            content="Biological Sex"
            width="287.5px"
            mobileWidth="334px"
            :options="genderOptions"
            :value="this.cardioQnr.basic_info.gender"
            @onChange="handleGenderChange"
            :disabled="this.patientHasGender || this.qnrIsCompleted"
          />
        </div>
        <div class="subpartContainer">
          <div class="title">Height (inches)</div>
          <el-input
            v-model="this.cardioQnr.basic_info.height"
            @input="handleValueChange('basic_info.height', $event)"
            class="inputField"
            :disabled="this.qnrIsCompleted"
          />
        </div>
        <div class="subpartContainer">
          <div class="title">Weight (lbs)</div>
          <el-input
            v-model="this.cardioQnr.basic_info.weight"
            @input="handleValueChange('basic_info.weight', $event)"
            class="inputField"
            :disabled="this.qnrIsCompleted"
          />
        </div>
        <div class="subpartContainer col-container">
          <QuestionnaireRadio
            content="Ethnicity"
            width="fit-content"
            mobileWidth="334px"
            :options="ethnicityOptions"
            :value="this.cardioQnr.basic_info.ethnicity"
            @onChange="handleValueChange('basic_info.ethnicity', $event)"
            :disabled="this.qnrIsCompleted"
          />
        </div>
        <div
          class="w-full"
          v-if="this.cardioQnr.basic_info.ethnicity === 'OTHER'"
        >
          <div class="expandedContainer">
            <div class="mb-[10px]">Please specify</div>
            <el-input
              v-model="this.cardioQnr.basic_info.other_ethnicity"
              class="expandInputField"
              :type="this.dynamic_input_type"
              :disabled="this.qnrIsCompleted"
              @input="handleValueChange('basic_info.other_ethnicity', $event)"
            />
          </div>
        </div>
        <div class="questionnaireContainer mt-[-18px]">
          <CardioRelatedQuestions
            :showWarning="this.showWarning"
            @removeWarning="removeWarning"
          />
        </div>
        <div class="confirmContainer" v-loading="loadingSubmit">
          <el-button
            class="saveButton"
            @click="this.saveQuestionnaire"
            :disabled="this.qnrIsCompleted"
          >
            Save for Now</el-button
          >
          <el-button
            class="submitQuestionnaireButton"
            @click="checkAllFieldsFilled"
            :disabled="this.qnrIsCompleted"
            >Submit</el-button
          >
        </div>
      </div>
      <img id="footerImage" alt="Footer Image" src="@/assets/footerImage.svg" />
    </div>
  </div>
  <el-dialog
    v-model="showSubmitDialog"
    center
    modal-class="hormoneDialog confirmDialog"
  >
    <div class="dialogTitle">Are you sure to submit your questionnaire?</div>
    <div class="dialogContent">
      Results can not be modified after your submission.
    </div>
    <template #footer>
      <span class="dialog-footer">
        <el-button class="cancelBtn" @click="this.showSubmitDialog = false"
          >Cancel</el-button
        >
        <el-button class="submitBtn" @click="this.confirmSubmitQuestionnaire">
          Confirm
        </el-button>
      </span>
    </template>
  </el-dialog>
  <el-dialog
    v-model="showSaveDialog"
    center
    modal-class="hormoneDialog confirmDialog"
  >
    <div class="dialogTitle">
      We have saved your results but haven't submitted.
    </div>
    <SimplifiedNotification
      :content="completedNotification"
      build_in_theme="yellow"
      :with_close_btn="false"
      class="mb-4"
    />
    <div class="dialogContent">
      You can come back here again via email link or PNS to continue filling in.
      Please click on <span class="font-medium">"Submit"</span> button once you
      finished this questionnaire.
    </div>
    <template #footer>
      <span class="dialog-footer">
        <el-button class="submitBtn" @click="this.showSaveDialog = false">
          Done
        </el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script>
import QuestionnaireHeader from "./QuestionnaireHeader.vue";
import QuestionnaireRadio from "./QuestionnaireRadio.vue";
import CardioRelatedQuestions from "./CardioRelatedQuestions.vue";

import { useCardioQnrStore } from "@/stores/CardioQnrStore.ts";
import { useCommonStore } from "@/stores/CommonStore.ts";
import { mapState, mapActions } from "pinia";
import {
  genderOptions,
  ethnicityOptions,
} from "@/utils/questionnaireOptions.js";
import moment from "moment";
import { useWindowSize } from "@vueuse/core";
import SimplifiedNotification from "@/components/commons/SimplifiedNotification.vue";

export default {
  name: "QuestionnaireView",
  components: {
    QuestionnaireHeader,
    QuestionnaireRadio,
    CardioRelatedQuestions,
    SimplifiedNotification,
  },
  props: {
    accession_id: String,
  },
  setup() {
    const { width } = useWindowSize();
    return {
      width,
    };
  },
  data() {
    return {
      genderOptions,
      ethnicityOptions,
      showSaveDialog: false,
      showSubmitDialog: false,
      birthDateToUpdate: "",
      showWarning: false,
      allFieldsFilled: false,
    };
  },
  async mounted() {
    // Reset the questionnaire store to clear any previous data
    const cardioQnrStore = useCardioQnrStore();
    cardioQnrStore.$reset();
    if (this.accession_id) {
      this.setAccessionId(this.accession_id);
    }
    try {
      await this.getCardioQnrData();
      this.birthDateToUpdate = this.birthdate;
    } catch (error) {
      console.error("Error fetching questionnaire data:", error);
    }
  },
  computed: {
    ...mapState(
      () => useCardioQnrStore(),
      [
        "gender",
        "birthdate",
        "cardioQnr",
        "loadingQuestionnaire",
        "loadingSubmit",
        "qnrIsCompleted",
        "patientHasGender",
        "patientHasDOB",
      ]
    ),
    isMobile() {
      console.log("windowWidth", this.windowWidth);
      return this.width <= 808;
    },
    dynamic_font_size() {
      return this.width <= 808 ? "14px" : "18px";
    },
    completedNotification() {
      return (
        "<span class='text-[" +
        this.dynamic_font_size +
        "]'>Draft responses are saved for <b>24</b> hours. After that, the data will expire and the questionnaire must be completed again.</span>"
      );
    },
    dynamic_input_type() {
      return this.isMobile ? "textarea" : "text";
    },
    ethnicityChoice() {
      return this.cardioQnr.basic_info.ethnicity;
    },
  },
  watch: {
    cardioQnr: {
      deep: true,
      handler: function (newValue) {
        if (newValue) {
          this.updateAllFieldsFilled();
        }
      },
    },
    birthDateToUpdate: {
      handler(newVal) {
        let cleaned = newVal.replace(/\D/g, "");
        if (cleaned.length > 8) {
          cleaned = cleaned.slice(0, 8);
        }
        let formatted = "";
        if (cleaned.length > 0) {
          formatted = cleaned.slice(0, 2);
        }
        if (cleaned.length > 2) {
          formatted += "/" + cleaned.slice(2, 4);
        }
        if (cleaned.length > 4) {
          formatted += "/" + cleaned.slice(4, 8);
        }
        if (formatted !== newVal) {
          this.birthDateToUpdate = formatted;
        }
      },
    },
    birthdate: {
      handler(newVal) {
        if (newVal) {
          this.birthDateToUpdate = newVal;
        }
      },
    },
    ethnicityChoice: {
      handler(newVal) {
        if (newVal !== "OTHER") {
          this.handleValueChange("basic_info.other_ethnicity", null);
        }
        if (newVal !== "ASIAN") {
          this.handleValueChange("basic_info.specific_asian_ethnicity", null);
        }
      },
    },
  },
  methods: {
    ...mapActions(
      () => useCardioQnrStore(),
      [
        "getCardioQnrData",
        "submitCardioQnr",
        "setGender",
        "setBirthdate",
        "setCardioQnrSingleField",
      ]
    ),
    ...mapActions(() => useCommonStore(), ["setAccessionId"]),
    handleGenderChange(value) {
      this.setGender(value);
    },
    handleValueChange(key, value) {
      if (key === "basic_info.height" || key === "basic_info.weight") {
        value = this.formatNumber(value);
      }
      this.setCardioQnrSingleField(key, value);
    },
    formatNumber(value) {
      return value.replace(/[^0-9]/g, "");
    },
    handleBirthDateChange() {
      if (
        !moment(this.birthDateToUpdate).isValid() ||
        this.birthDateToUpdate?.length !== 10
      ) {
        console.log("Invalid date");
        this.birthDateToUpdate = "";
        return;
      }
      this.setBirthdate(this.birthDateToUpdate);
    },
    saveQuestionnaire() {
      // pass true means only save for now
      this.submitCardioQnr(true);
      this.showSaveDialog = true;
    },
    confirmSubmitQuestionnaire() {
      // pass false means actual submit
      this.submitCardioQnr(false);
      this.showSubmitDialog = false;
    },
    checkAllFieldsFilled() {
      if (this.allFieldsFilled) {
        this.showSubmitDialog = true;
      } else {
        this.showWarning = true;
        this.$nextTick(() => {
          console.log("scrolling to required fields");
          const element = document.getElementById("required-questions");
          if (element) {
            element.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        });
      }
    },
    removeWarning() {
      this.showWarning = false;
    },
    updateAllFieldsFilled() {
      let allFieldsNeeded = {
        blood_pressure_checked:
          this.cardioQnr.medical_history.blood_pressure_checked,
      };
      if (this.cardioQnr.medical_history.blood_pressure_checked) {
        allFieldsNeeded = {
          ...allFieldsNeeded,
          systolic:
            this.cardioQnr.medical_history.systolic,
          diastolic:
            this.cardioQnr.medical_history.diastolic,
        };
      }
      if (allFieldsNeeded) {
        this.allFieldsFilled = Object.values(allFieldsNeeded).every(
          (field) => field !== null && field !== undefined
        );
      } else {
        this.allFieldsFilled = true;
      }
    },
  },
};
</script>

<style scoped>
.pageBodyContainer {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 26px;
  min-height: 1000px;
  position: relative;
}

.title {
  font-weight: 600;
}

.questionnaireContent {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 840px;
}

.subTitleContainer {
  padding: 12px 24px;
  border-radius: 4px;
  background-color: #faece6;
  margin-top: 20px;
  margin-bottom: 24px;
}

.subTitle {
  color: #0e4672;
  font-family: Roboto;
  font-weight: 500;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0.17px;
}

.subpartContainer {
  width: 100%;
  padding: 0 16px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
}

.col-container {
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 20px;
}

.confirmContainer {
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
  margin-bottom: 300px;
}

.saveButton {
  border: 1px solid #0e4672;
  background: #ffffff;
  margin-right: 10px;
  width: 395px;
  height: 42px;
  color: #0e4672;
  font-size: 16px;
  font-weight: 500;
  line-height: 19px;
  letter-spacing: 0.4px;
}

.submitQuestionnaireButton {
  border: 1px solid #0e4672;
  background: #0e4672;
  width: 395px;
  height: 42px;
  color: #ffffff;
  font-size: 16px;
  font-weight: 500;
  line-height: 19px;
  letter-spacing: 0.4px;
}

.saveButton:hover {
  border: 1px solid #5194d2;
  color: #5194d2;
}

.submitQuestionnaireButton:hover {
  border: 1px solid #5194d2;
  background: #5194d2;
}

.saveButton.is-disabled {
  background-color: #ffffff !important;
  border: #bbbbbb 1px solid !important ;
  color: #bbbbbb !important;
}

.submitQuestionnaireButton.is-disabled {
  background-color: #f3f3f3 !important;
  border-color: #f3f3f3 !important;
  color: #bbbbbb !important;
}

.dialogTitle {
  font-size: 24px;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: 0.46px;
  text-align: center;
  color: #0e4672;
  margin-bottom: 10px;
}

.dialogContent {
  font-size: 24px;
  font-weight: 400;
  line-height: 28px;
  letter-spacing: 0.46px;
  text-align: center;
  color: #0e4672;
}

.inputField {
  width: 594px;
  height: 42px;
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  letter-spacing: 0.4px;
  color: #000000;
  border-radius: 4px;
}

:deep(.el-input__wrapper) {
  box-shadow: none;
  border: 1px solid #bcccdc;
  padding-left: 21px;
}

:deep(.el-input.is-disabled .el-input__wrapper) {
  background-color: #f2f2f2;
}

:deep(.el-input.is-disabled .el-input__inner) {
  -webkit-text-fill-color: #1c1c1c;
}

.expandedContainer {
  padding: 12px 24px;
  border-radius: 4px;
  background-color: #f3f6f8;
  margin-left: 12px;
  margin-right: 12px;
  margin-top: -10px;
}

:deep(.el-textarea__inner) {
  caret-color: #000 !important;
  white-space: normal;
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  letter-spacing: 0.4px;
  color: #000000;
  resize: none;
  height: 126px;
  box-shadow: none;
  padding: 12px 16px;
  border: 1px solid #bcccdc;
}

@media only screen and (max-width: 808px) {
  .expandedContainer {
    padding: 12px 16px;
    margin: 0px 15px 10px 8px;
    font-size: 14px;
    width: auto;
  }

  .expandedContainer :deep(.el-textarea__inner) {
    height: 100px;
    font-size: 14px;
    line-height: 16px;
  }
  .subpartContainer {
    flex-direction: column;
    align-items: flex-start;
    padding: 0 8px;
    margin-bottom: 12px;
  }

  .subpartContainer .title {
    font-size: 14px;
  }

  .questionnaireContainer {
    width: 100%;
  }

  .inputField {
    width: 334px;
    height: 42px;
    font-size: 14px;
    line-height: 14px;
    letter-spacing: 0.4px;
  }

  #footerImage {
    display: none;
  }

  .questionnaireContent {
    width: 358px;
  }

  .confirmContainer {
    flex-direction: column;
    margin-top: 24px;
    margin-bottom: 50px;
  }

  .saveButton {
    width: 358px;
    margin-bottom: 10px;
    font-size: 14px;
  }

  .submitQuestionnaireButton {
    width: 358px;
    margin-left: 0px !important;
    font-size: 14px;
  }

  .dialogContent {
    font-size: 16px;
    line-height: 20px;
    letter-spacing: 0.4px;
  }
}
</style>
